import React from "react"

interface QuestionSkeletonProps {
  type?: "text" | "dropdown" | "radio" | "checkbox" | "date" | "upload"
  count?: string
}

const QuestionSkeleton: React.FC<QuestionSkeletonProps> = ({
  type = "text",
  count = 1,
}) => {
  const Skeleton = ({ className }: { className: string }) => (
    <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
  )

  const renderSkeleton = () => {
    switch (type) {
      case "dropdown":
        return (
          <div className="space-y-3">
            <Skeleton className="w-48 h-4" /> {/* Question title */}
            <div className="relative">
              <Skeleton className="w-full h-10 rounded-md" /> {/* Dropdown */}
              <div className="absolute top-3 right-3">
                <Skeleton className="w-4 h-4" /> {/* Chevron icon */}
              </div>
            </div>
          </div>
        )

      case "radio":
      case "checkbox":
        return (
          <div className="space-y-3">
            <Skeleton className="w-48 h-4" /> {/* Question title */}
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <Skeleton
                    className={`${
                      type === "radio" ? "rounded-full" : "rounded"
                    } h-5 w-5`}
                  />
                  <Skeleton className="w-32 h-4" />
                </div>
              ))}
            </div>
          </div>
        )

      case "date":
        return (
          <div className="space-y-3">
            <Skeleton className="w-48 h-4" /> {/* Question title */}
            <div className="flex gap-2 items-end">
              <div className="flex-1 space-y-1">
                <Skeleton className="w-12 h-3" /> {/* Month label */}
                <Skeleton className="w-full h-10 rounded-md" />{" "}
                {/* Month dropdown */}
              </div>
              <div className="flex-1 space-y-1">
                <Skeleton className="w-8 h-3" /> {/* Day label */}
                <Skeleton className="w-full h-10 rounded-md" />{" "}
                {/* Day dropdown */}
              </div>
              <div className="flex-1 space-y-1">
                <Skeleton className="w-10 h-3" /> {/* Year label */}
                <Skeleton className="w-full h-10 rounded-md" />{" "}
                {/* Year dropdown */}
              </div>
              <Skeleton className="w-10 h-10 rounded-md" />{" "}
              {/* Calendar icon */}
            </div>
          </div>
        )

      case "upload":
        return (
          <div className="space-y-3">
            <Skeleton className="w-48 h-4" /> {/* Question title */}
            <div className="p-6 rounded-lg border-2 border-gray-300 border-dashed">
              <div className="space-y-3 text-center">
                <Skeleton className="mx-auto w-16 h-16 rounded-lg" />{" "}
                {/* Upload icon */}
                <Skeleton className="mx-auto w-40 h-4" /> {/* Upload text */}
                <Skeleton className="mx-auto w-32 h-3" /> {/* File type text */}
              </div>
            </div>
            <Skeleton className="w-64 h-3" /> {/* Help text */}
          </div>
        )

      default: // "text"
        return (
          <div className="space-y-3">
            <Skeleton className="w-48 h-4" /> {/* Question title */}
            <Skeleton className="w-full h-10 rounded-md" /> {/* Text input */}
          </div>
        )
    }
  }

  return (
    <div className="space-y-6">
      {[...Array(count)].map((_, i) => (
        <div key={i}>{renderSkeleton()}</div>
      ))}
    </div>
  )
}

// Internal skeleton components (not exported individually)
const TextQuestionSkeleton = () => <QuestionSkeleton type="text" />
const DropdownQuestionSkeleton = () => <QuestionSkeleton type="dropdown" />
const RadioQuestionSkeleton = () => <QuestionSkeleton type="radio" />
const CheckboxQuestionSkeleton = () => <QuestionSkeleton type="checkbox" />
const DateQuestionSkeleton = () => <QuestionSkeleton type="date" />
const UploadQuestionSkeleton = () => <QuestionSkeleton type="upload" />

// Form loading skeleton with mixed question types
export const FormLoadingSkeleton = () => (
  <div className="space-y-8">
    <TextQuestionSkeleton />
    <TextQuestionSkeleton />
    <TextQuestionSkeleton />
    <RadioQuestionSkeleton />
    <UploadQuestionSkeleton />
    <DropdownQuestionSkeleton />
    <CheckboxQuestionSkeleton />
    <DateQuestionSkeleton />
  </div>
)

export { QuestionSkeleton }
